import { useEffect, useState } from 'react'
import { User, Send, X, CheckCircle2, AlertCircle, Users, TrendingUp, Target, Calendar } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { useLanguage } from '@/contexts/LanguageContext'
import { getApiBaseUrl } from '@/lib/apiBaseUrl'
import { supabase } from '@/lib/supabase'
import { getSlotsForDay, sessionTitleFromSlot, WEEKDAY_LABELS, type ScheduleSlot } from '@/lib/tkbcSchedule'

export interface TrainerFeedback {
  payload_version: 'trainer_feedback.v1'
  trainer_id: string
  session_id: string
  client_id: string
  timestamp: string

  // Client Performance Assessment
  client_performance: {
    technique_quality: number      // 1-10
    effort_level: number            // 1-10
    improvement_visible: boolean
    areas_excelling: string[]       // ["kicks", "defense", "conditioning"]
    areas_needing_work: string[]    // ["balance", "power", "speed"]
  }

  // Training Session Evaluation
  session_evaluation: {
    difficulty_appropriate: 'too_easy' | 'optimal' | 'too_hard'
    duration_appropriate: 'too_short' | 'optimal' | 'too_long'
    exercises_effective: boolean
    progression_suitable: boolean
    safety_concerns: string[]       // ["knee", "shoulder", "none"]
  }

  // Client Progress Tracking
  progress_assessment: {
    skill_improvement: number       // 1-10 compared to last session
    fitness_improvement: number      // 1-10
    motivation_level: number        // 1-10
    consistency_rating: number      // 1-10 (attendance, effort)
    goal_progress: number            // 1-10 towards stated goals
  }

  // Recommendations
  recommendations: {
    next_session_focus: string[]    // ["power", "technique", "cardio"]
    adjustments_needed: string[]    // ["reduce intensity", "add recovery"]
    equipment_suggestions: string[] // ["heavy_bag", "pads"]
    timeline_adjustments: string    // "accelerate", "maintain", "slow_down"
  }

  // Coaching Notes
  coaching_notes: {
    positive_observations: string
    areas_for_improvement: string
    motivational_insights: string
    technical_corrections: string
    overall_assessment: string
  }

  // Future Planning
  future_planning: {
    next_goals: string[]
    periodization_phase: 'build' | 'peak' | 'deload' | 'maintain'
    weeks_until_next_milestone: number
    priority_focus: string
  }
}

interface TrainerSessionOption {
  id: string
  label: string
}

interface TrainerClientOption {
  id: string
  label: string
  name?: string
}

interface TrainerFeedbackSubmissionResult {
  sessionId: string
  clientId: string
  clientName?: string
}

type TrainerPrefillTemplate = 'none' | 'technique' | 'conditioning'
type TrainerClientMode = 'known' | 'manual' | 'guest'
type TrainerSessionMode = 'known' | 'new'

interface TrainerFeedbackInputProps {
  sessionId: string
  trainerId: string
  clientId: string
  clientName?: string
  sessionOptions?: TrainerSessionOption[]
  clientOptions?: TrainerClientOption[]
  prefillTemplate?: TrainerPrefillTemplate
  quickActionKey?: number
  enableGuestClientMode?: boolean
  onFeedbackSubmitted?: (result: TrainerFeedbackSubmissionResult) => void
}

export default function TrainerFeedbackInput({
  sessionId,
  trainerId,
  clientId,
  clientName,
  sessionOptions = [],
  clientOptions = [],
  prefillTemplate = 'none',
  quickActionKey = 0,
  enableGuestClientMode = true,
  onFeedbackSubmitted
}: TrainerFeedbackInputProps) {
  const { t, language } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [validationMessage, setValidationMessage] = useState<string | null>(null)
  const [selectedSessionId, setSelectedSessionId] = useState<string>(sessionId)
  const [sessionMode, setSessionMode] = useState<TrainerSessionMode>(sessionOptions.length > 0 ? 'known' : 'new')
  const [newSessionTitle, setNewSessionTitle] = useState<string>('')
  const [newSessionDate, setNewSessionDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [selectedClientId, setSelectedClientId] = useState<string>(clientId)
  const [selectedClientName, setSelectedClientName] = useState<string>(clientName || '')
  const [clientMode, setClientMode] = useState<TrainerClientMode>(clientOptions.length > 0 ? 'known' : 'manual')
  const [manualClientId, setManualClientId] = useState<string>(clientId || '')
  const [manualClientName, setManualClientName] = useState<string>(clientName || '')
  const [guestAlias, setGuestAlias] = useState<string>('')

  useEffect(() => {
    if (sessionMode === 'known') {
      setSelectedSessionId(sessionId)
    }
  }, [sessionId])

  useEffect(() => {
    if (sessionOptions.length === 0 && sessionMode === 'known') {
      setSessionMode('new')
    }
  }, [sessionMode, sessionOptions.length])

  useEffect(() => {
    setSelectedClientId(clientId)
    setManualClientId(clientId || '')
  }, [clientId])

  useEffect(() => {
    setSelectedClientName(clientName || '')
    setManualClientName(clientName || '')
  }, [clientName])

  useEffect(() => {
    if (clientOptions.length === 0 && clientMode === 'known') {
      setClientMode('manual')
    }
  }, [clientMode, clientOptions.length])

  useEffect(() => {
    if (clientMode === 'known') {
      const selectedFromOptions = clientOptions.find(option => option.id === selectedClientId)
      const fallbackOption = selectedFromOptions || clientOptions[0]
      if (fallbackOption) {
        setSelectedClientId(fallbackOption.id)
        setSelectedClientName(fallbackOption.name || fallbackOption.label)
      }
      return
    }

    if (clientMode === 'manual') {
      setSelectedClientId(manualClientId.trim())
      setSelectedClientName(manualClientName.trim())
      return
    }

    const normalizedAlias = guestAlias
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    const guestId = normalizedAlias ? `guest:${normalizedAlias}` : ''
    setSelectedClientId(guestId)
    setSelectedClientName(guestAlias.trim() ? `Gast: ${guestAlias.trim()}` : 'Gast')
  }, [clientMode, clientOptions, guestAlias, manualClientId, manualClientName, selectedClientId])

  useEffect(() => {
    if (sessionMode === 'known') {
      const selectedFromOptions = sessionOptions.find(option => option.id === selectedSessionId)
      const fallbackOption = selectedFromOptions || sessionOptions[0]
      if (fallbackOption) {
        setSelectedSessionId(fallbackOption.id)
      }
      return
    }

    const normalizedTitle = newSessionTitle
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    const safeDate = (newSessionDate || new Date().toISOString().split('T')[0]).replace(/[^0-9-]/g, '')
    const generatedId = normalizedTitle
      ? `session:new:${safeDate}:${normalizedTitle}`
      : `session:new:${safeDate}:${Date.now()}`
    setSelectedSessionId(generatedId)
  }, [newSessionDate, newSessionTitle, selectedSessionId, sessionMode, sessionOptions])

  useEffect(() => {
    if (!quickActionKey) {
      return
    }

    if (prefillTemplate === 'technique') {
      setNextSessionFocus(['technique', 'speed'])
      setAdjustmentsNeeded(['more technique work'])
      setPriorityFocus('Technique refinement')
    }

    if (prefillTemplate === 'conditioning') {
      setNextSessionFocus(['cardio', 'endurance'])
      setAdjustmentsNeeded(['more conditioning'])
      setPriorityFocus('Cardio capacity')
    }

    setIsOpen(true)
  }, [quickActionKey, prefillTemplate])

  // Client Performance
  const [techniqueQuality, setTechniqueQuality] = useState<number>(5)
  const [effortLevel, setEffortLevel] = useState<number>(5)
  const [improvementVisible, setImprovementVisible] = useState<boolean>(false)
  const [areasExcelling, setAreasExcelling] = useState<string[]>([])
  const [areasNeedingWork, setAreasNeedingWork] = useState<string[]>([])

  // Session Evaluation
  const [difficultyAppropriate, setDifficultyAppropriate] = useState<'too_easy' | 'optimal' | 'too_hard'>('optimal')
  const [durationAppropriate, setDurationAppropriate] = useState<'too_short' | 'optimal' | 'too_long'>('optimal')
  const [exercisesEffective, setExercisesEffective] = useState<boolean>(true)
  const [progressionSuitable, setProgressionSuitable] = useState<boolean>(true)
  const [safetyConcerns, setSafetyConcerns] = useState<string[]>([])

  // Progress Assessment
  const [skillImprovement, setSkillImprovement] = useState<number>(5)
  const [fitnessImprovement, setFitnessImprovement] = useState<number>(5)
  const [motivationLevel, setMotivationLevel] = useState<number>(5)
  const [consistencyRating, setConsistencyRating] = useState<number>(5)
  const [goalProgress, setGoalProgress] = useState<number>(5)

  // Recommendations
  const [nextSessionFocus, setNextSessionFocus] = useState<string[]>([])
  const [adjustmentsNeeded, setAdjustmentsNeeded] = useState<string[]>([])
  const [equipmentSuggestions, setEquipmentSuggestions] = useState<string[]>([])
  const [timelineAdjustments, setTimelineAdjustments] = useState<string>('maintain')

  // Coaching Notes
  const [positiveObservations, setPositiveObservations] = useState<string>('')
  const [areasForImprovement, setAreasForImprovement] = useState<string>('')
  const [motivationalInsights, setMotivationalInsights] = useState<string>('')
  const [technicalCorrections, setTechnicalCorrections] = useState<string>('')
  const [overallAssessment, setOverallAssessment] = useState<string>('')

  // Future Planning
  const [nextGoals, setNextGoals] = useState<string[]>([])
  const [periodizationPhase, setPeriodizationPhase] = useState<'build' | 'peak' | 'deload' | 'maintain'>('maintain')
  const [weeksUntilMilestone, setWeeksUntilMilestone] = useState<number>(4)
  const [priorityFocus, setPriorityFocus] = useState<string>('')

  const handleArrayToggle = (
    array: string[],
    setArray: (arr: string[]) => void,
    value: string
  ) => {
    if (array.includes(value)) {
      setArray(array.filter(item => item !== value))
    } else {
      setArray([...array, value])
    }
  }

  const validateForm = (): string | null => {
    if (sessionMode === 'new' && newSessionTitle.trim().length < 3) {
      return t('trainer.feedback.validationNewSession') || 'Bitte gib fuer die neue Session einen Titel mit mindestens 3 Zeichen ein.'
    }

    if (clientMode === 'guest' && guestAlias.trim().length < 2) {
      return t('trainer.feedback.validationGuestAlias') || 'Bitte gib fuer den Gast mindestens 2 Zeichen als Namen ein.'
    }

    if (!selectedSessionId || !selectedClientId) {
      return t('trainer.feedback.validationSelection') || 'Bitte Session und Client auswaehlen.'
    }

    const scoreValues = [techniqueQuality, effortLevel, skillImprovement, fitnessImprovement, motivationLevel, consistencyRating, goalProgress]
    const hasInvalidScore = scoreValues.some((score) => Number.isNaN(score) || score < 1 || score > 10)
    if (hasInvalidScore) {
      return t('trainer.feedback.validationScores') || 'Alle Score-Felder muessen zwischen 1 und 10 liegen.'
    }

    if (weeksUntilMilestone < 1 || weeksUntilMilestone > 52) {
      return t('trainer.feedback.validationWeeks') || 'Weeks until milestone muss zwischen 1 und 52 liegen.'
    }

    const hasActionableInput =
      nextSessionFocus.length > 0 ||
      adjustmentsNeeded.length > 0 ||
      priorityFocus.trim().length > 0 ||
      areasForImprovement.trim().length > 0 ||
      overallAssessment.trim().length > 0

    if (!hasActionableInput) {
      return t('trainer.feedback.validationActionable') || 'Bitte mindestens eine konkrete Empfehlung oder Beobachtung erfassen.'
    }

    return null
  }

  const handleSubmit = async () => {
    const invalidReason = validateForm()
    if (invalidReason) {
      setValidationMessage(invalidReason)
      setSubmitStatus('error')
      return
    }

    setSubmitting(true)
    setSubmitStatus('idle')
    setValidationMessage(null)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('Trainer not authenticated')
      }

      const trainerFeedback: TrainerFeedback = {
        payload_version: 'trainer_feedback.v1',
        trainer_id: trainerId,
        session_id: selectedSessionId,
        client_id: selectedClientId,
        timestamp: new Date().toISOString(),
        client_performance: {
          technique_quality: techniqueQuality,
          effort_level: effortLevel,
          improvement_visible: improvementVisible,
          areas_excelling: areasExcelling,
          areas_needing_work: areasNeedingWork
        },
        session_evaluation: {
          difficulty_appropriate: difficultyAppropriate,
          duration_appropriate: durationAppropriate,
          exercises_effective: exercisesEffective,
          progression_suitable: progressionSuitable,
          safety_concerns: safetyConcerns
        },
        progress_assessment: {
          skill_improvement: skillImprovement,
          fitness_improvement: fitnessImprovement,
          motivation_level: motivationLevel,
          consistency_rating: consistencyRating,
          goal_progress: goalProgress
        },
        recommendations: {
          next_session_focus: nextSessionFocus,
          adjustments_needed: adjustmentsNeeded,
          equipment_suggestions: equipmentSuggestions,
          timeline_adjustments: timelineAdjustments
        },
        coaching_notes: {
          positive_observations: positiveObservations,
          areas_for_improvement: areasForImprovement,
          motivational_insights: motivationalInsights,
          technical_corrections: technicalCorrections,
          overall_assessment: overallAssessment
        },
        future_planning: {
          next_goals: nextGoals,
          periodization_phase: periodizationPhase,
          weeks_until_next_milestone: weeksUntilMilestone,
          priority_focus: priorityFocus
        }
      }

      // Submit to backend
      const response = await fetch(`${getApiBaseUrl()}/b2b/trainer-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify(trainerFeedback)
      })

      if (!response.ok) {
        throw new Error('Failed to submit trainer feedback')
      }

      setSubmitStatus('success')
      setTimeout(() => {
        setIsOpen(false)
        onFeedbackSubmitted?.({
          sessionId: selectedSessionId,
          clientId: selectedClientId,
          clientName: selectedClientName
        })
        // Reset form
        setTechniqueQuality(5)
        setEffortLevel(5)
        setImprovementVisible(false)
        setAreasExcelling([])
        setAreasNeedingWork([])
        setDifficultyAppropriate('optimal')
        setDurationAppropriate('optimal')
        setExercisesEffective(true)
        setProgressionSuitable(true)
        setSafetyConcerns([])
        setSkillImprovement(5)
        setFitnessImprovement(5)
        setMotivationLevel(5)
        setConsistencyRating(5)
        setGoalProgress(5)
        setNextSessionFocus([])
        setAdjustmentsNeeded([])
        setEquipmentSuggestions([])
        setTimelineAdjustments('maintain')
        setPositiveObservations('')
        setAreasForImprovement('')
        setMotivationalInsights('')
        setTechnicalCorrections('')
        setOverallAssessment('')
        setNextGoals([])
        setPeriodizationPhase('maintain')
        setWeeksUntilMilestone(4)
        setPriorityFocus('')
      }, 2000)
    } catch (error) {
      console.error('Error submitting trainer feedback:', error)
      setValidationMessage(t('trainer.feedback.error') || 'Fehler beim Übermitteln des Feedbacks')
      setSubmitStatus('error')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 shadow-lg"
        variant="primary"
      >
        <User className="w-5 h-5 mr-2" />
        {t('trainer.feedback.submit') || 'Trainer Feedback'}
      </Button>
    )
  }

  const commonAreas = ['kicks', 'punches', 'defense', 'footwork', 'conditioning', 'balance', 'power', 'speed', 'endurance', 'technique']
  const focusOptions = ['power', 'technique', 'cardio', 'flexibility', 'strength', 'speed', 'endurance']
  const adjustmentOptions = ['reduce intensity', 'increase intensity', 'add recovery', 'more technique work', 'more conditioning']
  const equipmentOptions = ['heavy_bag', 'thai_pads', 'focus_mitts', 'medicine_ball', 'none']

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                {t('trainer.feedback.title') || 'Trainer Feedback'}
              </h2>
              {clientName && (
                <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                  {t('trainer.feedback.client') || 'Client'}: {clientName}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={() => setIsOpen(false)}
              className="p-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
              {t('trainer.feedback.session') || 'Session'}
            </label>
            <div className="mb-3 flex flex-wrap gap-2">
              <button
                type="button"
                className={`px-3 py-1 rounded-lg text-xs ${
                  sessionMode === 'known'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                }`}
                onClick={() => setSessionMode('known')}
                disabled={sessionOptions.length === 0}
              >
                {t('trainer.feedback.sessionModeKnown') || 'Bestehende Session'}
              </button>
              <button
                type="button"
                className={`px-3 py-1 rounded-lg text-xs ${
                  sessionMode === 'new'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                }`}
                onClick={() => setSessionMode('new')}
              >
                {t('trainer.feedback.sessionModeNew') || 'Neue Session'}
              </button>
            </div>

            {sessionMode === 'known' && sessionOptions.length > 0 && (
              <select
                value={selectedSessionId}
                onChange={(e) => setSelectedSessionId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
              >
                {sessionOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {sessionMode === 'new' && (
              <div className="space-y-3">
                {/* Schedule suggestions for selected date */}
                {(() => {
                  const selectedDate = newSessionDate ? new Date(newSessionDate + 'T12:00:00') : new Date()
                  const dayOfWeek = selectedDate.getDay()
                  const slots = getSlotsForDay(dayOfWeek)
                  const dayName = WEEKDAY_LABELS[dayOfWeek]?.[language === 'de' ? 'de' : 'en'] || ''

                  if (slots.length === 0) return null

                  return (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <div className="text-xs font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {language === 'de'
                          ? `Stundenplan ${dayName}`
                          : `Schedule ${dayName}`}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {slots.map((slot: ScheduleSlot, idx: number) => (
                          <button
                            key={idx}
                            type="button"
                            className="px-2 py-1 rounded text-xs bg-blue-100 dark:bg-blue-800/40 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-700/50 transition-colors"
                            onClick={() => setNewSessionTitle(sessionTitleFromSlot(slot, language === 'de' ? 'de' : 'en'))}
                          >
                            {slot.startTime} {slot.title[language === 'de' ? 'de' : 'en']}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })()}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                      {t('trainer.feedback.newSessionTitle') || 'Session Titel'}
                    </label>
                    <Input
                      type="text"
                      value={newSessionTitle}
                      onChange={(e) => setNewSessionTitle(e.target.value)}
                      placeholder={t('trainer.feedback.newSessionTitlePlaceholder') || 'z.B. Technikblock Dienstag Abend'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                      {t('trainer.feedback.newSessionDate') || 'Session Datum'}
                    </label>
                    <Input
                      type="date"
                      value={newSessionDate}
                      onChange={(e) => setNewSessionDate(e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2 text-xs text-gray-500 dark:text-slate-400">
                    {(t('trainer.feedback.newSessionGeneratedId') || 'Generierte Session-ID')}: {selectedSessionId}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
              {t('trainer.feedback.client') || 'Client'}
            </label>

            {enableGuestClientMode && (
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`px-3 py-1 rounded-lg text-xs ${
                    clientMode === 'known'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                  }`}
                  onClick={() => setClientMode('known')}
                  disabled={clientOptions.length === 0}
                >
                  {t('trainer.feedback.clientModeKnown') || 'Bestehender Client'}
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-lg text-xs ${
                    clientMode === 'manual'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                  }`}
                  onClick={() => setClientMode('manual')}
                >
                  {t('trainer.feedback.clientModeManual') || 'Manuell'}
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-lg text-xs ${
                    clientMode === 'guest'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                  }`}
                  onClick={() => setClientMode('guest')}
                >
                  {t('trainer.feedback.clientModeGuest') || 'Gast-Modus'}
                </button>
              </div>
            )}

            {clientMode === 'known' && clientOptions.length > 0 && (
              <select
                value={selectedClientId}
                onChange={(e) => {
                  const nextClient = clientOptions.find(option => option.id === e.target.value)
                  setSelectedClientId(e.target.value)
                  if (nextClient?.name) {
                    setSelectedClientName(nextClient.name)
                  } else if (nextClient?.label) {
                    setSelectedClientName(nextClient.label)
                  }
                }}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
              >
                {clientOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}

            {clientMode === 'manual' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.client') || 'Client'} ID
                  </label>
                  <Input
                    type="text"
                    value={manualClientId}
                    onChange={(e) => setManualClientId(e.target.value)}
                    placeholder="client-id"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.clientNameOptional') || 'Client Name (optional)'}
                  </label>
                  <Input
                    type="text"
                    value={manualClientName}
                    onChange={(e) => setManualClientName(e.target.value)}
                    placeholder={t('trainer.feedback.clientNamePlaceholder') || 'Client Name'}
                  />
                </div>
              </div>
            )}

            {clientMode === 'guest' && (
              <div className="space-y-2">
                <Input
                  type="text"
                  value={guestAlias}
                  onChange={(e) => setGuestAlias(e.target.value)}
                  placeholder={t('trainer.feedback.guestAliasPlaceholder') || 'Gastname eingeben (z.B. Probetraining Max)'}
                />
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {(t('trainer.feedback.guestGeneratedId') || 'Generierte Gast-ID')}: {selectedClientId || 'guest:...'}
                </p>
              </div>
            )}
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-green-800 dark:text-green-200">
                {t('trainer.feedback.success') || 'Feedback erfolgreich übermittelt!'}
              </span>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200">
                {validationMessage || t('trainer.feedback.error') || 'Fehler beim Übermitteln des Feedbacks'}
              </span>
            </div>
          )}

          <div className="space-y-6">
            {/* Client Performance */}
            <section>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100 flex items-center gap-2">
                <Target className="w-5 h-5" />
                {t('trainer.feedback.clientPerformance') || 'Client Performance'}
              </h3>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.techniqueQuality') || 'Technik-Qualität'} (1-10)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={techniqueQuality}
                    onChange={(e) => setTechniqueQuality(parseInt(e.target.value) || 5)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.effortLevel') || 'Anstrengungs-Level'} (1-10)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={effortLevel}
                    onChange={(e) => setEffortLevel(parseInt(e.target.value) || 5)}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2 mb-2 text-gray-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={improvementVisible}
                    onChange={(e) => setImprovementVisible(e.target.checked)}
                    className="w-4 h-4"
                  />
                  {t('trainer.feedback.improvementVisible') || 'Verbesserung sichtbar'}
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.areasExcelling') || 'Bereiche: Exzellent'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {commonAreas.map(area => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => handleArrayToggle(areasExcelling, setAreasExcelling, area)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          areasExcelling.includes(area)
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                        }`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.areasNeedingWork') || 'Bereiche: Verbesserung nötig'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {commonAreas.map(area => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => handleArrayToggle(areasNeedingWork, setAreasNeedingWork, area)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          areasNeedingWork.includes(area)
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                        }`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Session Evaluation */}
            <section>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                {t('trainer.feedback.sessionEvaluation') || 'Session Evaluation'}
              </h3>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.difficulty') || 'Schwierigkeit'}
                  </label>
                  <select
                    value={difficultyAppropriate}
                    onChange={(e) => setDifficultyAppropriate(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                  >
                    <option value="too_easy">{t('trainer.feedback.tooEasy') || 'Zu leicht'}</option>
                    <option value="optimal">{t('trainer.feedback.optimal') || 'Optimal'}</option>
                    <option value="too_hard">{t('trainer.feedback.tooHard') || 'Zu schwer'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.duration') || 'Dauer'}
                  </label>
                  <select
                    value={durationAppropriate}
                    onChange={(e) => setDurationAppropriate(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                  >
                    <option value="too_short">{t('trainer.feedback.tooShort') || 'Zu kurz'}</option>
                    <option value="optimal">{t('trainer.feedback.optimal') || 'Optimal'}</option>
                    <option value="too_long">{t('trainer.feedback.tooLong') || 'Zu lang'}</option>
                  </select>
                </div>
              </div>

              <div className="mb-4 space-y-2">
                <label className="flex items-center gap-2 text-gray-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={exercisesEffective}
                    onChange={(e) => setExercisesEffective(e.target.checked)}
                    className="w-4 h-4"
                  />
                  {t('trainer.feedback.exercisesEffective') || 'Übungen waren effektiv'}
                </label>
                <label className="flex items-center gap-2 text-gray-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={progressionSuitable}
                    onChange={(e) => setProgressionSuitable(e.target.checked)}
                    className="w-4 h-4"
                  />
                  {t('trainer.feedback.progressionSuitable') || 'Progression war angemessen'}
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                  {t('trainer.feedback.safetyConcerns') || 'Sicherheitsbedenken'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {['knee', 'shoulder', 'back', 'ankle', 'wrist', 'none'].map(concern => (
                    <button
                      key={concern}
                      type="button"
                      onClick={() => handleArrayToggle(safetyConcerns, setSafetyConcerns, concern)}
                      className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                        safetyConcerns.includes(concern)
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                      }`}
                    >
                      {concern}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Progress Assessment */}
            <section>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100 flex items-center gap-2">
                <Users className="w-5 h-5" />
                {t('trainer.feedback.progressAssessment') || 'Progress Assessment'}
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: t('trainer.feedback.skillImprovement') || 'Skill Improvement', value: skillImprovement, setter: setSkillImprovement },
                  { label: t('trainer.feedback.fitnessImprovement') || 'Fitness Improvement', value: fitnessImprovement, setter: setFitnessImprovement },
                  { label: t('trainer.feedback.motivationLevel') || 'Motivation Level', value: motivationLevel, setter: setMotivationLevel },
                  { label: t('trainer.feedback.consistencyRating') || 'Consistency Rating', value: consistencyRating, setter: setConsistencyRating },
                  { label: t('trainer.feedback.goalProgress') || 'Goal Progress', value: goalProgress, setter: setGoalProgress }
                ].map(({ label, value, setter }) => (
                  <div key={label}>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                      {label} (1-10)
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={value}
                      onChange={(e) => setter(parseInt(e.target.value) || 5)}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Recommendations */}
            <section>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100">
                {t('trainer.feedback.recommendations') || 'Recommendations'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.nextSessionFocus') || 'Next Session Focus'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {focusOptions.map(focus => (
                      <button
                        key={focus}
                        type="button"
                        onClick={() => handleArrayToggle(nextSessionFocus, setNextSessionFocus, focus)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          nextSessionFocus.includes(focus)
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                        }`}
                      >
                        {focus}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.adjustmentsNeeded') || 'Adjustments Needed'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {adjustmentOptions.map(adj => (
                      <button
                        key={adj}
                        type="button"
                        onClick={() => handleArrayToggle(adjustmentsNeeded, setAdjustmentsNeeded, adj)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          adjustmentsNeeded.includes(adj)
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                        }`}
                      >
                        {adj}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.equipmentSuggestions') || 'Equipment Suggestions'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {equipmentOptions.map(eq => (
                      <button
                        key={eq}
                        type="button"
                        onClick={() => handleArrayToggle(equipmentSuggestions, setEquipmentSuggestions, eq)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          equipmentSuggestions.includes(eq)
                            ? 'bg-indigo-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-slate-300'
                        }`}
                      >
                        {eq}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.timelineAdjustments') || 'Timeline Adjustments'}
                  </label>
                  <select
                    value={timelineAdjustments}
                    onChange={(e) => setTimelineAdjustments(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                  >
                    <option value="accelerate">{t('trainer.feedback.accelerate') || 'Beschleunigen'}</option>
                    <option value="maintain">{t('trainer.feedback.maintain') || 'Beibehalten'}</option>
                    <option value="slow_down">{t('trainer.feedback.slowDown') || 'Verlangsamen'}</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Coaching Notes */}
            <section>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100">
                {t('trainer.feedback.coachingNotes') || 'Coaching Notes'}
              </h3>

              <div className="space-y-4">
                {[
                  { label: t('trainer.feedback.positiveObservations') || 'Positive Observations', value: positiveObservations, setter: setPositiveObservations },
                  { label: t('trainer.feedback.areasForImprovement') || 'Areas for Improvement', value: areasForImprovement, setter: setAreasForImprovement },
                  { label: t('trainer.feedback.motivationalInsights') || 'Motivational Insights', value: motivationalInsights, setter: setMotivationalInsights },
                  { label: t('trainer.feedback.technicalCorrections') || 'Technical Corrections', value: technicalCorrections, setter: setTechnicalCorrections },
                  { label: t('trainer.feedback.overallAssessment') || 'Overall Assessment', value: overallAssessment, setter: setOverallAssessment }
                ].map(({ label, value, setter }) => (
                  <div key={label}>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                      {label}
                    </label>
                    <textarea
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                      rows={3}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Future Planning */}
            <section>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100">
                {t('trainer.feedback.futurePlanning') || 'Future Planning'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.periodizationPhase') || 'Periodization Phase'}
                  </label>
                  <select
                    value={periodizationPhase}
                    onChange={(e) => setPeriodizationPhase(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
                  >
                    <option value="build">{t('trainer.feedback.build') || 'Build'}</option>
                    <option value="peak">{t('trainer.feedback.peak') || 'Peak'}</option>
                    <option value="deload">{t('trainer.feedback.deload') || 'Deload'}</option>
                    <option value="maintain">{t('trainer.feedback.maintain') || 'Maintain'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.weeksUntilMilestone') || 'Weeks Until Next Milestone'}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="52"
                    value={weeksUntilMilestone}
                    onChange={(e) => setWeeksUntilMilestone(parseInt(e.target.value) || 4)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-slate-300">
                    {t('trainer.feedback.priorityFocus') || 'Priority Focus'}
                  </label>
                  <Input
                    type="text"
                    value={priorityFocus}
                    onChange={(e) => setPriorityFocus(e.target.value)}
                    placeholder={t('trainer.feedback.priorityFocusPlaceholder') || 'e.g., Power development, Technique refinement'}
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Submit Button */}
          <div className="mt-6 flex justify-end gap-4">
            <Button
              variant="ghost"
              onClick={() => setIsOpen(false)}
              disabled={submitting}
            >
              {t('common.cancel') || 'Abbrechen'}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || !selectedSessionId || !selectedClientId}
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {t('common.submitting') || 'Wird übermittelt...'}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {t('trainer.feedback.submit') || 'Feedback absenden'}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
